/**
 * Ink and tint helpers for customer brand colours on the Genie Page.
 *
 * The page paints with whatever colours a business uploaded, which means every
 * "brand colour + white text" assumption fails the day someone's brand is
 * pastel yellow. Anything that sits text or an icon on a brand colour asks
 * `inkOn` instead of assuming white.
 */

import { deepen, parseHex } from "@/utils/branding/brandContrast";
import {
  getBrandThemeFromColors,
  type GenieBrandTheme,
} from "@/utils/branding/genieBrandTheme";
import type { HelpHubBranding } from "@/types/help-hub/help-hub";

export { deepen, inkOn } from "@/utils/branding/brandContrast";

/** The brand colour at a given opacity, for soft background washes. */
export const tint = (brandColor: string, alpha: number): string => {
  const rgb = parseHex(brandColor);
  if (!rgb) return `rgba(78, 156, 255, ${alpha})`;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

/**
 * The Genie Page's first-fold background, painted in the genie's own brand.
 * It carries white text, so each colour goes through `deepen`; a second colour
 * is used only when the genie has its gradient switched on.
 */
export const heroBackground = (theme: GenieBrandTheme): string => {
  const primary = deepen(theme.primary);
  if (!theme.gradientOn) return primary;
  return `linear-gradient(110deg, ${primary}, ${deepen(theme.secondary)})`;
};

/**
 * The one brand theme for a Genie Page — same resolution as the owner-side
 * `getGenieBrandTheme`, fed from the public DTO. Every gradient, accent and
 * wash on the page derives from this so the whole surface agrees on what the
 * genie's brand is, and honors `gradientEnabled`/`gradientAngle` the moment
 * the endpoint sends them.
 */
export const hubBrandTheme = (branding: HelpHubBranding): GenieBrandTheme =>
  getBrandThemeFromColors(branding, { defaultGradientOn: true, defaultAngle: 90 });
